import {pdfjs} from "react-pdf";
import React, {createRef, ReactNode, RefObject} from "react";
import PortalDocument, {PortalDocumentInterface} from "../../document/PortalDocument";
import MouseMode from "../settings/MouseMode";
import DomHelper from "../../../shared/helper/DomHelper";
import {Attachment, Metrics, Rectangle} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import PortalContext from "../../PortalContext";
import Immutable from "../../../shared/immutable/Immutable";
import DocumentSettings from "../../document/DocumentSettings";
import UnitHelper from "../../../shared/helper/UnitHelper";
import LengthUnit from "../../../shared/units/LengthUnit";
import {DebugGlobals} from "../../dialog/components/settings/DebugDialog";
import Portal from "../../Portal";
import Timer from "../../../shared/class/Timer";
import PortalDocumentManager from "../../document/PortalDocumentManager";
import {isMobile} from "react-device-detect";
import MenuButton from "../../../shared/menu/MenuButton";
import Translate from "../../../shared/translation/Translate";
import {I18nGlobals} from "../../../shared/translation/i18nInterface";
import PortalPureComponent from "../../../shared/components/PortalPureComponent";

export class PortalOperationTextSelection extends Rectangle {
	page: number;
	timestamp: number;

	constructor(data: any) {
		super(data);

		this.page = data.page;
		this.timestamp = data.timestamp;
	}
}

export interface RangeInterface {
	page: number;
	startNode: string;
	startOffset: number;
	endNode: string;
	endOffset: number;
}

export interface TextLayerGlobals {
	textSelection: Immutable<Array<PortalOperationTextSelection>>;
	lastSelection: RangeInterface | null;
}

export function initTextLayerGlobals(): TextLayerGlobals {
	return {
		textSelection: Immutable.from([]),
		lastSelection: null
	};
}

export function handleTextLayerSelectionChange(page: Portal): void {
	new Timer(() => {
		let textSelection: Selection | null = window.getSelection();
		if (!FunctionHelper.exists(textSelection) || FunctionHelper.isEmpty(textSelection!.toString()) ||
			textSelection?.isCollapsed) {
			page.setState(initTextLayerGlobals());
		}
	}, 10).start();
}

interface TextLayerProperties extends I18nGlobals, DebugGlobals, TextLayerGlobals {
	pageNumber: number;
	currentPage: number;
	zoomValue: number;
	context: PortalContext;
	mouseMode: MouseMode;
	portalDocument: Immutable<PortalDocumentInterface>;
	attachmentKey?: string;
}

interface TextLayerStatus {
	currentTextSelection: Immutable<Array<PortalOperationTextSelection>>;
}

export class TextLayerDefinition extends PortalPureComponent<TextLayerProperties, TextLayerStatus> {
	private textLayerContainer: RefObject<HTMLDivElement>;

	/**
	 * pdf page constructor
	 */
	constructor(props: Readonly<TextLayerProperties>) {
		super(props);

		this.state = {
			currentTextSelection: Immutable.from([])
		};

		this.textLayerContainer = createRef<HTMLDivElement>();

		this.handleContextmenu = this.handleContextmenu.bind(this);
		this.handleKeydown = this.handleKeydown.bind(this);
		this.handleSelectionChange = this.handleSelectionChange.bind(this);
		this.buildSelectionRect = this.buildSelectionRect.bind(this);
		this.updateTextLayer = FunctionHelper.defer(this.updateTextLayer.bind(this));
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		window.addEventListener("contextmenu", this.handleContextmenu);
		window.addEventListener('keydown', this.handleKeydown);
		window.addEventListener("pointerup", this.handleSelectionChange);
		window.addEventListener("keyup", this.handleSelectionChange);

		await this.updateTextLayer();
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();

		window.removeEventListener("contextmenu", this.handleContextmenu);
		window.removeEventListener("keydown", this.handleKeydown);
		window.removeEventListener("pointerup", this.handleSelectionChange);
		window.removeEventListener("keyup", this.handleSelectionChange);
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<TextLayerProperties>, prevState: Readonly<any>
	): Promise<void> {
		let documentChanged: Array<boolean> = [
			this.props.portalDocument.get().id !== prevProps.portalDocument.get().id,
			this.props.portalDocument.get().updated !== prevProps.portalDocument.get().updated,
			this.props.zoomValue !== prevProps.zoomValue
		];

		if (documentChanged.includes(true)) {
			await this.updateTextLayer();
		}
	}

	private handleContextmenu(event: MouseEvent): boolean {
		if (!isMobile) {
			return true;
		}

		this.handleSelectionChange();

		// prevent mobile popup when selecting text
		event.preventDefault();
		event.stopPropagation();
		return false;
	}

	private handleKeydown(event: KeyboardEvent): void {
		if ((this.props.mouseMode !== MouseMode.TEXT && this.props.mouseMode !== MouseMode.SELECT) ||
			this.textLayerContainer.current === null) {
			return;
		}

		if (event.ctrlKey && event.key === "a" && this.props.currentPage === this.props.pageNumber) {
			event.preventDefault();

			let currentSelection: Selection | null = window.getSelection();
			if (currentSelection === null) {
				return;
			}

			// clear current selection
			currentSelection.removeAllRanges();

			// select all text contents
			for (let textNode of this.textLayerContainer.current.childNodes) {
				for (let textContent of textNode.childNodes) {
					let selectionRange = document.createRange();
					selectionRange.selectNodeContents(textContent);
					currentSelection.addRange(selectionRange);
				}
			}

			this.handleSelectionChange();
		}
	}

	private buildSelectionRect(bounds: DOMRect): Rectangle {
		if (!FunctionHelper.exists(this.textLayerContainer.current)) {
			return new Rectangle({});
		}

		let zoomScale: number = this.props.zoomValue / 100;
		let layerRect: DOMRect = this.textLayerContainer.current!.getBoundingClientRect();

		return Rectangle.fromJson({
			x: Math.round((bounds.left - layerRect.left) / zoomScale),
			y: Math.round((bounds.top - layerRect.top) / zoomScale),
			width: Math.round(bounds.width / zoomScale),
			height: Math.round(bounds.height / zoomScale),
			metrics: Metrics.Px
		} as Rectangle)
	}

	private handleSelectionChange(): void {
		let textSelection: Selection | null = window.getSelection();
		if (this.props.mouseMode !== MouseMode.SELECT || !FunctionHelper.exists(this.textLayerContainer.current) ||
			!FunctionHelper.exists(textSelection) || FunctionHelper.isEmpty(textSelection?.toString()) ||
			textSelection?.isCollapsed || textSelection?.type.toLowerCase() !== "range") {
			this.setState({
				currentTextSelection: Immutable.from([])
			});
			return;
		}

		let textLayer: HTMLDivElement = this.textLayerContainer.current!;
		let boundaries: Array<Rectangle> = [];
		let lastSelection: RangeInterface = {
			startOffset: 0,
			endOffset: 0,
			page: this.props.pageNumber
		} as RangeInterface;

		for (let index = 0; index < textSelection!.rangeCount; index++) {
			let range: Range = textSelection!.getRangeAt(index);

			if (range.commonAncestorContainer.nodeType === 3 && textLayer.contains(range.commonAncestorContainer)) {
				let bounds: DOMRect = range.getBoundingClientRect();
				boundaries.push(this.buildSelectionRect(bounds));
			} else {
				if (range.startContainer.nodeType === 3 && textLayer.contains(range.startContainer)) {
					let selection = document.createRange();
					selection.setStart(range.startContainer, range.startOffset);
					selection.setEnd(range.startContainer, range.startContainer.textContent!.length);
					let bounds: DOMRect = selection.getBoundingClientRect();

					boundaries.push(this.buildSelectionRect(bounds));
				}

				if (range.endContainer.nodeType === 3 && textLayer.contains(range.endContainer)) {
					let selection = document.createRange();
					selection.setStart(range.endContainer, 0);
					selection.setEnd(range.endContainer, range.endOffset);
					let bounds: DOMRect = selection.getBoundingClientRect();

					boundaries.push(this.buildSelectionRect(bounds));
				}
			}

			if (range.startContainer.nodeType === 3) {
				lastSelection.startOffset = range.startOffset;
			}

			if (range.endContainer.nodeType === 3) {
				lastSelection.endOffset = range.endOffset;
			}
		}

		let textNodes: HTMLCollection = this.textLayerContainer.current!.children;
		let endOffset: number = lastSelection.endOffset;
		for (let [index, node] of Object.entries(textNodes)) {
			if (textSelection?.containsNode(node, true) && node.localName !== "br") {
				if (!FunctionHelper.exists(lastSelection.startNode)) {
					lastSelection.startNode = index;
				}

				lastSelection.endNode = index;
				if (lastSelection.endOffset === 0) {
					endOffset = FunctionHelper.applyValue(node.textContent?.length, 0);
				}
			}

			if (!textSelection?.containsNode(node, false)) {
				continue;
			}

			let bounds: DOMRect = node.getBoundingClientRect();
			boundaries.push(this.buildSelectionRect(bounds));
		}
		lastSelection.endOffset = endOffset;

		let selection: Array<PortalOperationTextSelection> = [];
		for (let range of boundaries) {
			if (range.x! < 0 || range.y! < 0 || range.width! <= 0 || range.height! <= 0) {
				continue;
			}

			selection.push({
				page: this.props.pageNumber,
				height: range.height,
				width: range.width,
				x: range.x,
				y: range.y,
				metrics: range.metrics,
				timestamp: new Date().getTime()
			} as PortalOperationTextSelection);
		}

		if (selection.length > 0) {
			this.activateMouseFunction();

			let state: any = {
				lastSelection: lastSelection
			};

			if (!isMobile) {
				state.textSelection = Immutable.from(selection);
			} else {
				this.setState({
					currentTextSelection: Immutable.from(selection)
				});
			}

			this.props.context.getPage().setState(state);
		} else {
			this.setState({
				currentTextSelection: Immutable.from([])
			});
		}
	}

	public activateMouseFunction() {
		let documentManager: PortalDocumentManager = this.props.context.documentManager;

		let selection: Array<PortalDocument> = documentManager.getSelection();
		for (let selected of selection) {
			let documentSettings: DocumentSettings = selected.getSettings();
			documentSettings.activeViewer.mouseFunctionActive = true;
		}

		documentManager.updateGlobalDocumentList();
	}

	private async loadViewerData(): Promise<pdfjs.PDFDocumentProxy> {
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);

		if (portalDocument.isPortfolioFile()) {
			let attachment: Attachment = portalDocument.getAttachment(this.props.attachmentKey!)!;
			return await portalDocument.loadViewerData(attachment);
		}

		return await portalDocument.loadViewerData();
	}

	/**
	 * create a div and render the PDF text content into it
	 * @private
	 */
	private async updateTextLayer(): Promise<void> {
		if (this.textLayerContainer.current === null) {
			return;
		}

		// clear text layer
		DomHelper.html(this.textLayerContainer.current, "");

		try {
			let documentProxy: pdfjs.PDFDocumentProxy = await this.loadViewerData();
			let pageProxy: pdfjs.PDFPageProxy = await documentProxy.getPage(this.props.pageNumber);
			let scaledViewport: pdfjs.PageViewport = pageProxy.getViewport({
				// pdfjs is using points so scale must be adapted to this
				scale: UnitHelper.unitToPx(
					this.props.zoomValue / 100, LengthUnit.POINTS, this.props.portalResolution
				)
			});

			let textContent: any = await pageProxy.getTextContent();

			if (!this.mounted) {
				return;
			}

			await pdfjs.renderTextLayer({
				container: this.textLayerContainer.current,
				textContent: textContent,
				viewport: scaledViewport
			}).promise;

			let lastSelection: RangeInterface | null = this.props.lastSelection;
			if (FunctionHelper.exists(lastSelection) && lastSelection?.page === this.props.pageNumber) {
				let textSelection: Selection | null = window.getSelection();

				let range: Range = new Range();
				let startNode: HTMLElement;
				let endNode: HTMLElement;

				let textNodes: HTMLCollection = this.textLayerContainer.current!.children;
				for (let [index, node] of Object.entries(textNodes)) {
					if (index === lastSelection!.startNode) {
						startNode = node as HTMLElement;
					}

					if (index === lastSelection!.endNode) {
						endNode = node as HTMLElement;
					}
				}

				if (FunctionHelper.exists(startNode!)) {
					range.setStart(startNode!.firstChild!, lastSelection!.startOffset);
				}

				if (FunctionHelper.exists(endNode!)) {
					range.setEnd(endNode!.firstChild!, lastSelection!.endOffset);
				}

				textSelection?.removeAllRanges();
				textSelection?.addRange(range);
				this.handleSelectionChange();
			}
		} catch (e: any) {
			// can be ignored here
		}
	}

	private renderPopup(): ReactNode {
		let textSelection: Array<PortalOperationTextSelection> = this.state.currentTextSelection.get();
		if (textSelection.length === 0) {
			return null;
		}

		let zoomScale: number = this.props.zoomValue / 100;
		let popupWidth: number = 0;
		let popupXPos: number = 0;
		let popupYPos: number = 0;
		for (let selection of textSelection) {
			if ((popupXPos === 0 && selection.x! > 0) || selection.x! < popupXPos) {
				popupXPos = selection.x!;
			}

			if ((popupYPos === 0 && selection.y! > 0) || selection.y! < popupYPos) {
				popupYPos = selection.y!;
			}

			if (selection.x! + selection.width! > popupWidth) {
				popupWidth = selection.x! + selection.width!;
			}
		}

		popupWidth -= popupXPos;

		return (
			<div className={"popup"} style={{
				width: popupWidth * zoomScale,
				top: popupYPos * zoomScale,
				left: popupXPos * zoomScale
			}}>
				<div className="popup-content">
					<MenuButton {...this.props} icon="icon icon-check"
								tooltip={Translate.value("buttons.confirm")}
								onClick={() => {
									this.props.context.getPage().setState({
										textSelection: this.state.currentTextSelection
									}, () => {
										this.setState({
											currentTextSelection: Immutable.from([])
										});
									});
								}}/>
					<MenuButton {...this.props} icon="icon icon-close"
								tooltip={Translate.value("buttons.cancel")}
								onClick={() => {
									this.setState({
										currentTextSelection: Immutable.from([])
									});
								}}/>
				</div>
			</div>
		);
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let textLayerClasses: Array<string> = ["text-layer"];

		if (this.props.mouseMode === MouseMode.TEXT || this.props.mouseMode === MouseMode.SELECT) {
			textLayerClasses.push("is-visible");
		}

		return (
			<div className={textLayerClasses.join(" ")}>
				<div className="layer-content" ref={this.textLayerContainer}/>
				{this.renderPopup()}
			</div>
		);
	}
}

const TextLayer = React.memo(TextLayerDefinition,
	(prevProps: Readonly<TextLayerProperties>, nextProps: Readonly<TextLayerProperties>) => {
		if (!nextProps.portalDocument.equals(prevProps.portalDocument) &&
			(
				nextProps.portalDocument.get().id !== prevProps.portalDocument?.get().id ||
				nextProps.portalDocument.get().updated !== prevProps.portalDocument?.get().updated
			)
		) {
			return false;
		}

		if (nextProps.attachmentKey !== prevProps.attachmentKey) {
			return false;
		}

		if (nextProps.zoomValue !== prevProps.zoomValue) {
			return false;
		}

		if (nextProps.currentPage !== prevProps.currentPage) {
			return false;
		}

		if (nextProps.pageNumber !== prevProps.pageNumber) {
			return false;
		}

		if (nextProps.mouseMode !== prevProps.mouseMode) {
			return false;
		}

		return true;
	}
);

export default TextLayer;